import {AvalancheReel} from '@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReel';
import {ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';
import {Symbols, SymbolsCreation} from 'src/reels/Symbols';
import {
	ObjectSize,
	ReelConstants,
	SymbolPosition,
} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {Easing, IEasingFunction} from '@fantasmagames/core/src/tween/Easing';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {GameAvalancheConstants, GameReelConstants} from 'src/reels/GameReelConstants';
import {ReelSymbol} from '@fantasmagames/slot/src/modules/reels/spin/ReelSymbol';
import {GameReelSymbol} from 'src/avalanche/GameReelSymbol';
import {AvalancheNewSymbol} from '@fantasmagames/slot/src/logic/SpinResult';
import {TopReelSymbol} from 'src/topreels/TopReelSymbol';

export class GameAvalancheReel extends AvalancheReel {
	private _movingThroughTheLineCallback: Function;
	private _respinAttentionTriggeredCallback: Function;
	private _lineYPosition: number;

	public static readonly INITIAL_REELS: GameSymbol[][] = [
		[
			new GameSymbol(Symbols.TOP_RS, {width: 1, height: 1}),
			new GameSymbol(Symbols.BN, {width: 1, height: 1}),
			new GameSymbol(Symbols.F7, {width: 1, height: 1}),
			new GameSymbol(Symbols.F9, {width: 1, height: 1}),
			new GameSymbol(Symbols.F8, {width: 1, height: 1}),
			new GameSymbol(Symbols.F7, {width: 1, height: 1}),
		],
		[
			new GameSymbol(Symbols.F9, {width: 1, height: 1}),
			new GameSymbol(Symbols.TOP_BN, {width: 1, height: 1}),
			new GameSymbol(Symbols.F7, {width: 1, height: 1}),
			new GameSymbol(Symbols.F9, {width: 1, height: 1}),
			new GameSymbol(Symbols.F8, {width: 1, height: 1}),
			new GameSymbol(Symbols.F7, {width: 1, height: 1}),
		],
		[
			new GameSymbol(Symbols.F9, {width: 1, height: 1}),
			new GameSymbol(Symbols.F8, {width: 1, height: 1}),
			new GameSymbol(Symbols.BN, {width: 1, height: 1}),
			new GameSymbol(Symbols.F9, {width: 1, height: 1}),
			new GameSymbol(Symbols.F8, {width: 1, height: 1}),
			new GameSymbol(Symbols.F7, {width: 1, height: 1}),
		],
		[
			new GameSymbol(Symbols.F9, {width: 1, height: 1}),
			new GameSymbol(Symbols.F8, {width: 1, height: 1}),
			new GameSymbol(Symbols.F7, {width: 1, height: 1}),
			new GameSymbol(Symbols.F9, {width: 1, height: 1}),
			new GameSymbol(Symbols.F8, {width: 1, height: 1}),
			new GameSymbol(Symbols.F7, {width: 1, height: 1}),
		],
		[
			new GameSymbol(Symbols.F9, {width: 1, height: 1}),
			new GameSymbol(Symbols.F8, {width: 1, height: 1}),
			new GameSymbol(Symbols.F7, {width: 1, height: 1}),
			new GameSymbol(Symbols.F9, {width: 1, height: 1}),
			new GameSymbol(Symbols.F8, {width: 1, height: 1}),
			new GameSymbol(Symbols.F7, {width: 1, height: 1}),
		],
	];

	// is there any symbols hanging in the air to drop down before new symbols will come up
	protected _hasAnythingToDropFillingTheVoid: boolean = false;

	public dropSymbols(symbols: GameSymbol[]): void {
		let index: number = 0;
		let offset: number = 0;
		let lastDelay: number = 0;
		let ifFirst: boolean = true;
		this.topVisibleSymbolIndex = this._rowsToShow - symbols.length;
		this.symbolsOnReel = symbols.length;
		for (let i = ReelConstants.MAX_ROW_AMOUNT - 1; i >= ReelConstants.MAX_ROW_AMOUNT - this._rowsToShow; i--) {
			const symbol = this._symbols[i] as GameReelSymbol;

			if (index < symbols.length) {
				const symIndex: number = symbols.length - 1 - index;
				offset += symbols[symIndex].size.height - 1;
				this.setSymbolInPosition(symbols, i, symIndex, offset);
				symbol.visible = true;
				const rowIndex: number = i - offset;
				const yToPos: number = this.getYPositionForRow(rowIndex);

				if (index > 2) { /* Is top reels */
					lastDelay = index * GameAvalancheConstants.dropInConfig.rowDelayTop + this._reelIndex * GameAvalancheConstants.dropInConfig.reelDelayTop;
					lastDelay += GameAvalancheConstants.dropInConfig.topDelay;
				} else {
					lastDelay = index * GameAvalancheConstants.dropInConfig.rowDelay + this._reelIndex * GameAvalancheConstants.dropInConfig.reelDelay;
				}

				const dropNewTween: Tween = this.dropNewSymbolToPosition(i, yToPos, lastDelay, GameAvalancheConstants.DROP_EASING);

				if (ifFirst) {
					dropNewTween.onComplete(() => {
						this.onFirstSymbolDropped();
					});
					ifFirst = false;
				}
				if (index === symbols.length - 1) {
					dropNewTween.onComplete(() => {
						this.onLastSymbolDropped();
					});
				}

				index++;
			} else {
				symbol.visible = false;
			}
		}
	}

	public dropOldSymbolsOut(): void {
		let index: number = 0;
		for (let i = ReelConstants.MAX_ROW_AMOUNT - 1; i >= ReelConstants.MAX_ROW_AMOUNT - this._rowsToShow; i--) {
			const symbol = (this._symbols[i] as GameReelSymbol);
			const delay: number = index * GameAvalancheConstants.dropOutConfig.rowDelay + this._reelIndex * GameAvalancheConstants.dropOutConfig.reelDelay;
			const dropOutTween: Tween = new Tween(symbol)
				.to({y: symbol.y + 1024}, GameAvalancheConstants.dropOutConfig.dropDuration)
				.delay(delay)
				.easing(Easing.Cubic.In)
				.onUpdate(() => {
					if (symbol.y >= GameReelConstants.getBottomSymbolSlot().height * GameReelConstants.ROW_AMOUNT) {
						symbol.removeIdleAnimation();
					}
				});
			this.add(dropOutTween);
			dropOutTween.start();
			index++;

			if (i === ReelConstants.MAX_ROW_AMOUNT - this._rowsToShow) {
				dropOutTween.onComplete(this.onLastSymbolDroppedOut.bind(this));
			}
		}
	}

	protected dropExistingSymbolWithLineCallback(symIndex: number, yPosition: number, delay: number, shake: boolean, newRowPos: number, isDroppingFromDivider: boolean): Tween {
		if (shake) {
			this._symbols[symIndex].container.rotation = (Math.PI / 180) * -4;
		}
		const dropNewTween: Tween = new Tween(this._symbols[symIndex])
			.to({y: yPosition}, GameAvalancheConstants.dropExistingConfig.dropDuration)
			.onUpdate(() => {
				const symbol: GameReelSymbol = (this._symbols[symIndex] as GameReelSymbol);
				if (!symbol.symbolMovedThroughLine && (symbol.y + GameReelConstants.getBottomSymbolSlot().height / 2) > this._lineYPosition) {
					this._movingThroughTheLineCallback && this._movingThroughTheLineCallback(symbol.symbolType, newRowPos);
					symbol.symbolMovedThroughLine = true;

					this.tryToAddRespinAttention(symbol);
				}
			})
			.easing(GameAvalancheConstants.DROP_EASING)
			.delay(delay);
		this.add(dropNewTween);
		dropNewTween.start();

		/* Symbols dropping from under divider (row 3) gets a quick alpha fade to avoid glitchy look */
		if (isDroppingFromDivider && !(this._symbols[symIndex] instanceof TopReelSymbol)) {
			this._symbols[symIndex].alpha = 0;
			const alphaTween: Tween = new Tween(this._symbols[symIndex])
				.to({alpha: 1}, GameAvalancheConstants.dropExistingConfig.dropDuration * 0.15)
				.easing(Easing.Quadratic.In)
				.delay(delay);
			this.add(alphaTween);
			alphaTween.start();
		}

		if (shake) {
			const symRotTween: Tween = new Tween(this._symbols[symIndex].container)
				.to({rotation: (Math.PI / 180) * 4}, 150)
				.repeat(GameAvalancheConstants.dropExistingConfig.dropDuration / 180)
				.yoyo(true)
				.delay(delay)
				.onComplete(() => {
					this._symbols[symIndex].container.rotation = 0;
				})
				.start();
			this.add(symRotTween);
			symRotTween.start();
		}

		return dropNewTween;
	}

	public dropElementsToFillVoids(): void {
		let offset: number = 0;
		let isFirstTween: boolean = true;
		this._hasAnythingToDropFillingTheVoid = false;
		for (let i = ReelConstants.MAX_ROW_AMOUNT - 1; i >= 0; i--) {
			// 1a. if the symbols is visible (not destroyed)
			if (this._symbols[i].visible) {
				const pos = this._symbols[i].positionOnReel;
				const height = this._symbols[i].size.height;
				// 2a. and it is not on the expected place
				if (pos !== offset + height - 1) {
					const moveOffset = pos - (offset + height - 1);
					const rowIndex = this.getPositionOnReel(pos, moveOffset);
					const newYPos: number = this.getYPositionForRow(rowIndex);
					const delay: number = (ReelConstants.MAX_ROW_AMOUNT - rowIndex - 1) * GameAvalancheConstants.dropExistingConfig.rowDelay + this._reelIndex * GameAvalancheConstants.dropExistingConfig.reelDelay;
					const isDroppingFromDivider: boolean = (rowIndex - moveOffset) === 2;

					// 3a. then we're dropping it down with a tween
					const tween: Tween = this.dropExistingSymbolWithLineCallback(i, newYPos, delay, false, rowIndex, isDroppingFromDivider);

					this._symbols[i].positionOnReel -= moveOffset;
					this._symbols[i].setContainerParameters();

					if (isFirstTween) {
						isFirstTween = false;
						this._hasAnythingToDropFillingTheVoid = true;
						tween.onComplete(() => {
							this.cleanAfterFall();
							this._onFillingVoidsFinished(this._reelIndex);
						});
					}
				}
				offset += height;
				this.topVisibleSymbolIndex = i;
			} else {
				// 1b. if this is a last element on reel and it is invisible it means
				// there is no symbols left on this reel
				// so top visible element has the index of top element + 1
				// so that next falling symbol we receive 1st position on the reel
				if (i === ReelConstants.MAX_ROW_AMOUNT - 1) {
					this.topVisibleSymbolIndex = ReelConstants.MAX_ROW_AMOUNT;
				}

				return;
			}
		}
	}

	/**
	 * adds new symbols after win lines were destroyed or re-spin took place
	 * @param symbols - array of new symbols
	 * @param onAddingFinishedCallback - callback for when symbols were added
	 */
	public addNewSymbols(symbols: AvalancheNewSymbol[], onAddingFinishedCallback: Function): void {
		symbols = symbols.sort((s1, s2) => s2.row - s1.row);
		this._onAddingSymbolsFinished = onAddingFinishedCallback;
		this.symbolsOnReel += symbols.length;

		let lastTween: Tween;
		let lastDelay: number = 0;

		let isFirst: boolean = true;
		let firstSymbolPos: SymbolPosition;
		let firstSymbolSize: number;

		if (symbols.length === 0) {
			onAddingFinishedCallback && onAddingFinishedCallback();
		}

		// going through all the symbols
		for (let i = 0; i < symbols.length; i++) {
			const symbol = symbols[i];
			// taking highest visible symbol on reel
			const prevSym: ReelSymbol = this._symbols[this.topVisibleSymbolIndex];
			let prevElementReelPos: number;
			// taking previous symbol's reel position or setting it to -1 so that next position will be 0
			if (prevSym) {
				prevElementReelPos = prevSym.positionOnReel;
			} else {
				prevElementReelPos = -1;
			}

			// taking a new symbol from the reel and setting it up
			const currentSym: ReelSymbol = this._symbols[this.topVisibleSymbolIndex - 1];
			currentSym.visible = true;
			currentSym.positionOnReel = prevElementReelPos + symbol.height;
			currentSym.size = {width: 1, height: symbol.height};
			currentSym.setSymbol(this.makeSizedSymbol(symbol.symbol, currentSym.size));
			currentSym.setContainerParameters();

			const rowIndex: number = ReelConstants.MAX_ROW_AMOUNT - (prevElementReelPos + 1 + symbol.height);

			// coming up with a new y position for this symbol and delay for dropping tween
			const yToPos: number = this.getYPositionForRow(rowIndex);

			lastDelay = (ReelConstants.MAX_ROW_AMOUNT - rowIndex - 4) * GameAvalancheConstants.dropExistingConfig.rowDelay + this._reelIndex * GameAvalancheConstants.dropExistingConfig.reelDelay;

			// making a tween to drop a symbol onto a reel
			lastTween = this.dropNewSymbolToPosition(
				this.topVisibleSymbolIndex - 1,
				yToPos,
				lastDelay,
				GameAvalancheConstants.DROP_EASING,
				GameAvalancheConstants.dropExistingConfig.dropDuration,
			);

			this.topVisibleSymbolIndex--;

			if (isFirst) {
				isFirst = false;

				firstSymbolPos = {x: this._reelIndex, y: ReelConstants.MAX_ROW_AMOUNT - (prevElementReelPos + 1)};
				firstSymbolSize = symbol.height;
			}
		}

		if (lastTween) {
			lastTween.onComplete(() => {
				// here is what's happening after symbols fallen down
				this.cleanAfterFall();
				if (this._onAddingSymbolsFinished) {
					this._onAddingSymbolsFinished();
					this._onAddingSymbolsFinished = null;
				}

				if (symbols.length > 0) {
					// playing dropped sound for the biggest symbol size on the screen
					this.playNewSymbolDroppingSound(this.getBiggestSymbolSizeOf(symbols), lastDelay);
				}
				// playing avalanche landing animation for the first falling symbol
				this.playAvalancheLandingAnimationAt(firstSymbolPos, firstSymbolSize);
			});
		}
	}

	protected addAllSymbolsIntoSprite(spr: PIXI.Sprite, resources: ResourcesLoader): void {
		SymbolsCreation.addAllSymbolsIntoSprite(spr, resources);
	}

	protected addBackgroundsIntoSprite(spr: PIXI.Sprite, resources: ResourcesLoader): void {

	}

	protected addForegroundIntoSprite(spr: PIXI.Sprite, resources: ResourcesLoader): void {

	}

	public addWinIndicationAt(position: SymbolPosition, isRightToLeft: boolean): void {
		(this._symbols[position.y] as GameReelSymbol).addWinIndication(isRightToLeft);
	}

	public removeWinIndicationAt(position: SymbolPosition): void {
		(this._symbols[position.y] as GameReelSymbol).removeWinIndication();
	}

	public removeAllWinIndications(): void {
		for (const sym of this._symbols) {
			(sym as GameReelSymbol).removeWinIndication();
		}
	}

	protected makeSizedSymbol(symbol: number, size: ObjectSize): number {
		return symbol;
	}

	protected playAvalancheLandingAnimationAt(pos: SymbolPosition, height: number): void {

	}

	protected playRemoveAnimationAt(sym: ReelSymbol, onCompleted: Function): void {
		(sym as GameReelSymbol).playWinRemoveAnimation(() => {
			sym.visible = false;
			onCompleted && onCompleted();
		});

		const fadeOutTween: Tween = new Tween(sym)
			.to({alpha: 0}, 200)
			.easing(Easing.Sinusoidal.InOut)
			.delay(50)
			.onComplete(() => {
				sym.hideSymbol();
				sym.alpha = 1;
				(sym as GameReelSymbol).makeCleanup();
			});
		this.add(fadeOutTween);
		fadeOutTween.start();
	}

	public fixSymbolsBeforeNewDrop(): void {
		for (let rowIndex = 0; rowIndex < ReelConstants.MAX_ROW_AMOUNT; rowIndex++) {
			(this._symbols[rowIndex] as GameReelSymbol).fixTopReelSymbolBeforeNew();
		}
	}

	public fixSymbolsDuringAvalanches(): void {
		for (let rowIndex = 0; rowIndex < ReelConstants.MAX_ROW_AMOUNT; rowIndex++) {
			(this._symbols[rowIndex] as GameReelSymbol).fixTopReelSymbol();
		}
	}

	protected createReel(initialReel: GameSymbol[]): void {
		for (let rowIndex = 0; rowIndex < ReelConstants.MAX_ROW_AMOUNT; rowIndex++) {
			const symbolsSprite: PIXI.Sprite = new PIXI.Sprite();
			this.addAllSymbolsIntoSprite(symbolsSprite, this._resources);

			/*	for (const child of symbolsSprite.children) {
					(child as PIXI.Sprite).anchor.set(0.5);
				}

				const symSize: SymbolSizes = GameReelConstants.getGameSymbolSize({x: 0, y: rowIndex});
				symbolsSprite.position.set(symSize.width / 2, symSize.height / 2);
	*/
			const symbolBackground: PIXI.Sprite = new PIXI.Sprite();
			this.addBackgroundsIntoSprite(symbolBackground, this._resources);

			const symbolForeground: PIXI.Sprite = new PIXI.Sprite();
			this.addForegroundIntoSprite(symbolForeground, this._resources);

			const symbol: ReelSymbol = this.createReelSymbol();

			symbol.init(symbolsSprite, symbolBackground, symbolForeground);
			symbol.setSpineUtils(this.spineUtils);
			symbol.setTimerUtils(this._timerUtils);
			this.setInitialSymbolAtPosition(symbol, rowIndex, this._initialStrip[rowIndex]);

			symbol.index = rowIndex;
			symbol.x = GameReelConstants.getReelsOffsetForRow(rowIndex).x + GameReelConstants.getGameSymbolSize({
				x: this._reelIndex,
				y: 0,
			}).width * this._reelIndex;
			symbol.y = this.getYPositionForRow(rowIndex);

			/*	symbol.pivot.set(GameReelConstants.getGameSymbolSize({x: 0, y: rowIndex}).width / - 2,
					GameReelConstants.getGameSymbolSize({x: 0, y: rowIndex}).height / -2);*/

			this._symbols[rowIndex] = symbol;
			this._container.addChild(symbol);
		}

		if (initialReel) {
			this.symbolsOnReel = initialReel.length;
		}

		for (let rowIndex = 0; rowIndex < ReelConstants.MAX_ROW_AMOUNT; rowIndex++) {
			this._symbols[rowIndex].positionOnReel = rowIndex;
			this._symbols[rowIndex].size = {width: 1, height: 1};
		}

		if (initialReel) {
			this.setupInitialReel(initialReel);
		} else {
			this.setupInitialReel(GameAvalancheReel.INITIAL_REELS[this._reelIndex]);
		}

		this.fixSymbolsBeforeNewDrop();
	}

	protected setupInitialReel(initialReel: GameSymbol[]): void {
		let index: number = 0;
		let reelPosition: number = 0;

		this.topVisibleSymbolIndex = 0;

		for (let rowIndex = ReelConstants.MAX_ROW_AMOUNT - 1; rowIndex >= 0; rowIndex--) {
			const sym = this._symbols[rowIndex];
			const pos = initialReel[initialReel.length - 1 - index];

			if (index < initialReel.length) {
				sym.size = pos.size;
				sym.setSymbol(this.makeSizedSymbol(pos.sym, pos.size));
				sym.positionOnReel = reelPosition + pos.size.height - 1;

				sym.y = this.getYPositionForRow(rowIndex);

				reelPosition += pos.size.height;
				sym.visible = true;
			} else {
				sym.visible = false;
			}
			index++;
		}
	}

	protected filterNullSymbols(reelSymbols: GameSymbol[]): GameSymbol[] {
		return reelSymbols;
	}

	/**
	 * counting up y position of a symbol on a reel
	 * @param indexOffset - symbol index on reel
	 * @param symHeight - symbol height
	 */
	protected getInitialSymbolPositionY(rowIndex: number, symHeight: number): number {
		return null;
	}

	protected getYPositionForRow(rowIndex: number): number {
		return GameReelConstants.getReelsOffsetForRow(rowIndex).y + GameReelConstants.getSpecificRowYSymbolPosition(rowIndex);
	}

	protected dropNewSymbolToPosition(
		symIndex: number,
		yPosition: number,
		delay: number,
		easing: IEasingFunction = Easing.Quadratic.In,
		duration: number = GameAvalancheConstants.dropInConfig.dropDuration,
	): Tween {
		const symbol = this._symbols[symIndex] as GameReelSymbol;
		symbol.y =
			yPosition - GameReelConstants.getTopSymbolSlot().height * ReelConstants.MAX_ROW_AMOUNT - GameReelConstants.getReelsOffsetForRow(symIndex).y;
		const dropNewTween: Tween = new Tween(symbol)
			.to({y: yPosition}, duration)
			.easing(easing)
			.delay(delay);
		this.add(dropNewTween);
		dropNewTween.start();

		const timeToBounce: number = GameAvalancheConstants.dropInConfig.dropDuration + delay - AvalancheReel.STOP_ANIMATION_TIMER_OFFSET;
		this.tryToAddRespinAttention(symbol, timeToBounce);

		symbol.symbolMovedThroughLine = symIndex >= 3;

		return dropNewTween;
	}

	protected tryToAddRespinAttention(symbol: GameReelSymbol, timeToBounce?: number): void {
		if (symbol.shouldHaveRespinAttentionAnimation()) {
			if (timeToBounce) {
				this._timerUtils.createTimer(() => {
					symbol.playRespinAttention();
					this.sendRespinAttentionTriggeredCallback();
				}, timeToBounce);
			} else {
				symbol.playRespinAttention();
				this.sendRespinAttentionTriggeredCallback();
			}
		} else if (symbol.shouldHaveWildIdleAnimation()) {
			symbol.addIdleAnimation();
		}
	}

	protected sendRespinAttentionTriggeredCallback(): void {
		this._respinAttentionTriggeredCallback && this._respinAttentionTriggeredCallback();
	}

	public removeScatterRespinAs(symPos: SymbolPosition): void {
		const sym = this._symbols[this.getSymbolOnReelIndex(symPos.y)] as GameReelSymbol;

		if (sym.symbolType === Symbols.RS || sym.symbolType === Symbols.TOP_RS) {
			sym.removeIdleAnimation();
			sym.removeStopAnimation();
			this.moveSymbolFromOversizedToReelContainer(sym);
			sym.hideSymbol();
			sym.visible = false;
		} else {
			throw new Error(`removeScatterRespinAs:: Not a scatter at ${symPos}`);
		}
	}

	public playRespinAnimationAt(symPos: SymbolPosition, symbol: number, onCompleted: Function): void {
		const sym = this._symbols[this.getSymbolOnReelIndex(symPos.y)];
		sym.visible = false;

		this._timerUtils.createTimer(() => {
			onCompleted && onCompleted();
		}, 200);
	}

	public removeRespinSymbol(positions: SymbolPosition[]): void {
		const topPosition: number = this.getSymbolOnReelIndex(0);

		positions = positions.sort((s1, s2) => s1.y - s2.y);

		for (const pos of positions) {
			const sym = this._symbols[topPosition + pos.y];
			this._symbols.splice(topPosition + pos.y, 1);
			this._symbols.unshift(sym);

			this.symbolsOnReel--;

			for (let rowIndex = 0; rowIndex < ReelConstants.MAX_ROW_AMOUNT; rowIndex++) {
				this._symbols[rowIndex].index = rowIndex;
			}
		}

		this.topVisibleSymbolIndex += positions.length;
	}

	/**
	 * getting reel symbols array index from y position of symbol
	 * @param yPosition - position of symbol
	 */
	protected getSymbolOnReelIndex(yPosition: number): number {
		return ReelConstants.MAX_ROW_AMOUNT - this.symbolsOnReel + yPosition;
	}

	protected createReelSymbol(): ReelSymbol {
		return new GameReelSymbol();
	}

	public get hasAnythingToDropFillingTheVoid(): boolean {
		return this._hasAnythingToDropFillingTheVoid;
	}

	/**
	 *    Get the symbolType of the symbol on y position.
	 * @param yPosition
	 */
	public getSymbolTypeOnReelIndex(yPosition: number): number {
		if (this.symbolsOnReel === 0) {
			return -1;
		}
		const index: number = this.getSymbolOnReelIndex(yPosition);
		if (index < 0 || index >= ReelConstants.ROW_AMOUNT) {
			return -1;
		}

		return this._symbols[this.getSymbolOnReelIndex(yPosition)].symbolType;
	}

	public getSymbolFromPosition(yPosition: number): GameReelSymbol {
		if (this.symbolsOnReel === 0) {
			return null;
		}
		const index: number = this.getSymbolOnReelIndex(yPosition);
		if (index < 0 || index >= ReelConstants.ROW_AMOUNT) {
			return null;
		}

		return this._symbols[this.getSymbolOnReelIndex(yPosition)] as GameReelSymbol;
	}

	public set lineYPosition(value: number) {
		this._lineYPosition = value;
	}

	public set movingThroughTheLineCallback(value: Function) {
		this._movingThroughTheLineCallback = value;
	}

	public set respinAttentionTriggeredCallback(value: Function) {
		this._respinAttentionTriggeredCallback = value;
	}

	public moveSymbolFromOversizedToReelContainer(symbol: GameReelSymbol): void {
		if (symbol.oversizedLayer && symbol.oversizedLayer.children.length > 0) {
			this._container.addChild(symbol.oversizedLayer.children[0]);
		}
	}

	public moveSymbolsFromOversizedToReelContainer(): void {
		for (const sym of this._symbols) {
			const symbol = (sym as GameReelSymbol);

			if (symbol.oversizedLayer && symbol.oversizedLayer.children.length > 0) {
				this._container.addChild(symbol.oversizedLayer.children[0]);
			}
		}
	}

	public setOversizedLayer(oversizedLayer: PIXI.Container): void {
		for (const sym of this._symbols) {
			(sym as GameReelSymbol).oversizedLayer = oversizedLayer;
		}
	}
}



// WEBPACK FOOTER //
// ./src/avalanche/GameAvalancheReel.ts